import React, { useState, useEffect } from 'react';
import './Main.css';

interface Conference {
  name: string;
  url: string;
  rating: string;
  category: string;
  submissionDeadline: string | string[];
}



type SortOption = 'rating' | 'deadline' | 'none';

interface ConferenceLinksProps {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

const ConferenceLinks: React.FC<ConferenceLinksProps> = ({ darkMode, onToggleDarkMode }) => {
  const [sortBy, setSortBy] = useState<SortOption>(() => {
    const savedSort = localStorage.getItem('conferenceSortBy');
    return (savedSort as SortOption) || 'none';
  });
  const [conferenceData, setConferenceData] = useState<{ 
    conferences: Conference[]; 
    categoryDisplayNames?: Record<string, string> 
  }>({ conferences: [] });
  const [pageTitle, setPageTitle] = useState('Conferences');
  const [loading, setLoading] = useState(true);
  const [showCollected, setShowCollected] = useState(() => {
    const saved = localStorage.getItem('conferenceLinksShowCollected');
    return saved === 'true';
  });
  const [collectedConferences, setCollectedConferences] = useState<string[]>(() => {
    const saved = localStorage.getItem('conferenceLinksCollected');
    return saved ? JSON.parse(saved) : [];
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadData = async () => {
      // Load configuration for title
      try {
        const configResponse = await fetch('/config.json');
        const config = await configResponse.json();
        if (config.conferenceLinksTitle) {
          setPageTitle(config.conferenceLinksTitle);
        }
      } catch (error) {
        console.error('Error loading config.json:', error);
      }

      // Load conference links data
      try {
        const response = await fetch('/conferences.json');
        const data = await response.json();
        setConferenceData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading conference data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem('conferenceSortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('conferenceLinksShowCollected', showCollected.toString());
  }, [showCollected]);

  useEffect(() => {
    localStorage.setItem('conferenceLinksCollected', JSON.stringify(collectedConferences));
  }, [collectedConferences]);

  const toggleCollect = (conferenceName: string) => {
    setCollectedConferences(prev => {
      if (prev.includes(conferenceName)) {
        return prev.filter(name => name !== conferenceName);
      } else {
        return [...prev, conferenceName];
      }
    });
  };

  const formatConferenceName = (name: string) => {
    const parts = name.split(' - ');
    if (parts.length === 2) {
      return (
        <>
          <strong>{parts[0]}</strong> - {parts[1]}
        </>
      );
    }
    return name;
  };

  const formatRating = (rating: string) => {
    if (rating === 'N/A') {
      return 'N/A';
    }
    return `CCF-${rating}`;
  };

  const parseDeadlineToDate = (deadlineStr: string): Date => {
    // Parse "Early January", "Middle March", etc. to actual dates
    const [period, month] = deadlineStr.split(' ');
    const months: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3,
      'May': 4, 'June': 5, 'July': 6, 'August': 7,
      'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    
    const currentYear = new Date().getFullYear();
    const monthIndex = months[month];
    let day = 15; // Default to middle of month
    
    if (period === 'Early') day = 10;
    else if (period === 'Middle') day = 15;
    else if (period === 'Late') day = 25;
    
    return new Date(currentYear, monthIndex, day);
  };

  const getNearestDeadline = (submissionDeadline: string | string[]): string => {
    if (typeof submissionDeadline === 'string') {
      return submissionDeadline;
    }
    
    const now = new Date();
    let nearestDeadline = submissionDeadline[0];
    let nearestDate = parseDeadlineToDate(submissionDeadline[0]);
    
    // If the deadline has passed, consider next year
    if (nearestDate < now) {
      nearestDate = new Date(nearestDate.getFullYear() + 1, nearestDate.getMonth(), nearestDate.getDate());
    }
    
    for (let i = 1; i < submissionDeadline.length; i++) {
      let currentDate = parseDeadlineToDate(submissionDeadline[i]);
      
      // If the deadline has passed, consider next year
      if (currentDate < now) {
        currentDate = new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate());
      }
      
      if (currentDate < nearestDate) {
        nearestDate = currentDate;
        nearestDeadline = submissionDeadline[i];
      }
    }
    
    return nearestDeadline;
  };

  const hasMultipleDeadlines = (submissionDeadline: string | string[]): boolean => {
    return Array.isArray(submissionDeadline) && submissionDeadline.length > 1;
  };

  const getMonthNumber = (deadline: string): number => {
    const months: { [key: string]: number } = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4,
      'May': 5, 'June': 6, 'July': 7, 'August': 8,
      'September': 9, 'October': 10, 'November': 11, 'December': 12
    };
    const [period, month] = deadline.split(' ');
    const monthNum = months[month] || 0;
    const periodWeight = period === 'Early' ? 0 : period === 'Middle' ? 1 : 2;
    return monthNum * 10 + periodWeight;
  };

  const sortConferences = (conferences: Conference[]): Conference[] => {
    if (sortBy === 'none') return conferences;
    
    return [...conferences].sort((a, b) => {
      if (sortBy === 'rating') {
        return a.rating.localeCompare(b.rating);
      } else if (sortBy === 'deadline') {
        const aNearestDeadline = getNearestDeadline(a.submissionDeadline);
        const bNearestDeadline = getNearestDeadline(b.submissionDeadline);
        return getMonthNumber(aNearestDeadline) - getMonthNumber(bNearestDeadline);
      }
      return 0;
    });
  };

  if (loading) {
    return <div className="loading">Loading conference data...</div>;
  }

  return (
    <div className="conference-links">
      <h1>{pageTitle}</h1>
      <div className="controls">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search conferences..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="controls-group">
          <label>
            Sort by:
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)}>
              <option value="none">No sorting</option>
              <option value="rating">Rating</option>
              <option value="deadline">Submission Deadline</option>
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={showCollected}
              onChange={(e) => setShowCollected(e.target.checked)}
            />
            Show Collected Only
          </label>
          <button onClick={onToggleDarkMode} className="theme-toggle">
            {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </div>
      </div>
      {(() => {
        // Group conferences by category
        const groupedConferences = conferenceData.conferences.reduce((acc, conf) => {
          const category = conf.category;
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(conf);
          return acc;
        }, {} as Record<string, Conference[]>);

        // Use category display names from JSON, with fallback to the category key itself
        const categoryNames = conferenceData.categoryDisplayNames || {};

        return Object.entries(groupedConferences).map(([categoryKey, conferences]) => {
          const filteredConferences = conferences.filter(conf => {
            const matchesSearch = conf.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCollected = !showCollected || collectedConferences.includes(conf.name);
            return matchesSearch && matchesCollected;
          });
          
          if (filteredConferences.length === 0) return null;

          return (
            <div key={categoryKey} className="conference-category">
              <h2>{categoryNames[categoryKey] || categoryKey}</h2>
              <ul>
                {sortConferences(filteredConferences).map((conference, confIndex) => (
                <li key={confIndex}>
                  <div className="conference-item">
                    <a href={conference.url} target="_blank" rel="noopener noreferrer">
                      {formatConferenceName(conference.name)}
                    </a>
                    <span className="conference-info">
                      <span className="rating">{formatRating(conference.rating)}</span>
                      <span className="deadline-container">
                        {hasMultipleDeadlines(conference.submissionDeadline) ? (
                          <span className="multiple-deadlines-wrapper">
                            <span className="deadline single-deadline">
                              {getNearestDeadline(conference.submissionDeadline)}
                              <span className="multiple-badge">+{Array.isArray(conference.submissionDeadline) ? conference.submissionDeadline.length - 1 : 0}</span>
                            </span>
                            <span className="all-deadlines-hover">
                              {Array.isArray(conference.submissionDeadline) 
                                ? conference.submissionDeadline.map((deadline, idx) => (
                                    <span 
                                      key={idx} 
                                      className={`deadline-item ${deadline === getNearestDeadline(conference.submissionDeadline) ? 'nearest' : 'other'}`}
                                    >
                                      {deadline}
                                    </span>
                                  ))
                                : conference.submissionDeadline
                              }
                              <span className="multiple-indicator">({Array.isArray(conference.submissionDeadline) ? conference.submissionDeadline.length : 1} rounds)</span>
                            </span>
                          </span>
                        ) : (
                          <span className="deadline single-deadline">
                            {getNearestDeadline(conference.submissionDeadline)}
                          </span>
                        )}
                      </span>
                    </span>
                    <button
                      onClick={() => toggleCollect(conference.name)}
                      className={`collect-btn ${collectedConferences.includes(conference.name) ? 'collected' : ''}`}
                      title={collectedConferences.includes(conference.name) ? 'Remove from collection' : 'Add to collection'}
                    >
                      {collectedConferences.includes(conference.name) ? '★' : '☆'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          );
        });
      })()}
      <footer style={{ 
        textAlign: 'center', 
        padding: '25px 20px',
        marginTop: '50px',
        color: '#666',
        fontSize: '0.9rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: 'transparent'
      }}>
        <div style={{ 
          maxWidth: '500px', 
          margin: '0 auto',
          lineHeight: '1.5'
        }}>
          ©{' '}
          <a 
            href="https://linmao.org" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: '#0066cc',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = '#004999';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = '#0066cc';
            }}
          >
            Mao Lin
          </a>
          {' '}{new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
};

export default ConferenceLinks; 